import { Save, Trash2 } from "lucide-react";
import React, { useEffect, useId, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import usersService, { User } from "../services/usersService";
import { canManageUserPermissions } from "../utils/userPermissions";
import httpService from "../services/httpService";
import type { HttpError } from "../services/httpService";
import ConfirmationModal from "./ConfirmationModal";
import UserPermissionsModal from "./UserPermissionsModal";
import AlphaSettingsModal from "../pages/alpha/AlphaSettingsModal";
import { alphaAdminUi } from "./admin/alphaAdminUi";

interface Provider {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  mode: "create" | "edit";
  onSave: (userData: Partial<User>) => Promise<void>;
  onDelete?: (userId: string) => Promise<void>;
  onManagePermissions?: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, user, mode, onSave, onDelete, onManagePermissions }) => {
  const { user: actor } = useAuth();
  const titleId = useId();
  const selection = isOpen ? `${actor?.id ?? ""}:${mode}:${user?.id ?? "new"}` : null;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const requestRef = useRef(0);
  const mutationRef = useRef(0);
  const [formData, setFormData] = useState({
    email: "", name: "", roles: ["INVESTOR"] as string[], emailVerified: false, providerId: "",
  });
  const [initialUser, setInitialUser] = useState<User | null>(null);
  const [loadedSelection, setLoadedSelection] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [choosingRoles, setChoosingRoles] = useState(false);

  const mayManagePermissions = () => isOpen && canManageUserPermissions(actor,
    actor?.roles?.includes("ADMIN") ? httpService.getAuthTokenPair()?.accessToken ?? null : null);
  const canEditPermissions = mayManagePermissions();
  const permissionAccessRef = useRef(canEditPermissions);
  permissionAccessRef.current = canEditPermissions;
  const isReady = mode === "create" || loadedSelection === selection;
  const profileDirty = mode === "edit" && initialUser !== null && (
    formData.name !== (initialUser.name ?? "") || formData.emailVerified !== initialUser.emailVerified
    || formData.providerId !== String(initialUser.provider?.id ?? initialUser.providerId ?? "")
  );
  const isSaveDisabled = loading || dataLoading || !isReady
    || (mode === "create" && (!canEditPermissions || !formData.roles.length))
    || (formData.roles.includes("PROVIDER") && !formData.providerId);

  useEffect(() => {
    if (!isOpen) return;
    const requestId = ++requestRef.current;
    let active = true;
    const invalidatePendingMutation = () => { ++mutationRef.current; };
    const owner = selection;
    const isCurrent = () => active && requestRef.current === requestId && selectionRef.current === owner;
    setLoading(false);
    setError(null);
    setInitialUser(null);
    setLoadedSelection(null);
    setShowDeleteConfirm(false);
    setChoosingRoles(false);
    setProviders([]);
    setFormData({ email: user?.email ?? "", name: user?.name ?? "",
      roles: mode === "create" ? ["INVESTOR"] : [], emailVerified: user?.emailVerified ?? false, providerId: "" });
    setDataLoading(mode === "edit");

    if (mode === "edit" && user) {
      usersService.getUserById(user.id).then(detail => {
        if (!isCurrent()) return;
        setInitialUser(detail);
        setFormData({ email: detail.email, name: detail.name ?? "", roles: [...(detail.roles ?? [])],
          emailVerified: detail.emailVerified, providerId: String(detail.provider?.id ?? detail.providerId ?? "") });
        setLoadedSelection(owner);
      }).catch((err: unknown) => {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : "Unable to load this user.");
      }).finally(() => {
        if (isCurrent()) setDataLoading(false);
      });
    }
    if (permissionAccessRef.current) {
      httpService.get<{ providers: Provider[] }>("/providers", { requestScope: "actor" }).then(response => {
        if (isCurrent()) setProviders((response.data.providers ?? []).map(provider => ({ ...provider, id: String(provider.id) })));
      }).catch(() => {
        if (isCurrent()) setProviders([]);
      });
    }
    return () => { active = false; invalidatePendingMutation(); };
  }, [isOpen, mode, user, selection, reload]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaveDisabled) return;
    const providerChanged = mode === "edit" && formData.providerId !== String(initialUser?.provider?.id ?? initialUser?.providerId ?? "");
    if ((providerChanged || mode === "create") && !mayManagePermissions()) {
      setError("Only an administrator with full administration access can change roles or provider assignments.");
      return;
    }
    const mutationId = ++mutationRef.current;
    const owner = selection;
    const isCurrent = () => mutationRef.current === mutationId && selectionRef.current === owner;
    setLoading(true);
    setError(null);
    try {
      await onSave({ email: formData.email, name: formData.name, emailVerified: formData.emailVerified,
        ...(mode === "create" ? { roles: formData.roles, providerId: formData.providerId } : {}),
        ...(mode === "edit" && canEditPermissions && formData.providerId !== String(initialUser?.provider?.id ?? initialUser?.providerId ?? "")
          ? { providerId: formData.providerId } : {}),
      });
      if (isCurrent()) onClose();
    } catch (err: unknown) {
      if (!isCurrent()) return;
      const denied = (err as HttpError | null)?.status === 403 || (err as HttpError | null)?.response?.status === 403;
      setError(denied ? "You do not have permission to save this user. Your draft has been kept."
        : err instanceof Error ? err.message : "Unable to save this user.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!user || !onDelete) return;
    const mutationId = ++mutationRef.current;
    const owner = selection;
    const isCurrent = () => mutationRef.current === mutationId && selectionRef.current === owner;
    setLoading(true);
    try {
      await onDelete(user.id);
      if (isCurrent()) onClose();
    } catch (err: unknown) {
      if (isCurrent()) setError(err instanceof Error ? err.message : "Unable to delete this user.");
    } finally {
      if (isCurrent()) { setLoading(false); setShowDeleteConfirm(false); }
    }
  };

  if (!isOpen) return null;

  return <>
    <AlphaSettingsModal isOpen={isOpen && !choosingRoles} onClose={onClose} title={mode === "create" ? "Create New User" : "Edit User"}
      maxWidthClass="max-w-xl" closeDisabled={loading || showDeleteConfirm} spacious
      description={mode === "create" ? "Create an account and choose its initial access." : "Update profile details. Manage access separately in Roles and scopes."}>
      <form onSubmit={handleSubmit} aria-label="User details" className="space-y-5">
        {dataLoading && <p role="status" className="text-sm text-v7-ink-soft">Loading user details...</p>}
        {!dataLoading && !isReady && <button type="button" onClick={() => setReload(value => value + 1)} className={alphaAdminUi.link}>Reload user</button>}
        {error && <p role="alert" className={alphaAdminUi.error}>{error}</p>}
        <div>
          <label htmlFor={`${titleId}-email`} className="mb-1 block text-sm font-medium text-v7-ink">Email Address *</label>
          <input type="email" id={`${titleId}-email`} required readOnly={mode === "edit"}
            value={isReady ? formData.email : user?.email ?? ""} disabled={loading || dataLoading || !isReady}
            onChange={e => setFormData(previous => ({ ...previous, email: e.target.value }))}
            className={alphaAdminUi.input} placeholder="user@example.com" />
          {mode === "edit" && <p className="mt-1 text-xs text-v7-ink/60">The login email is read-only here.</p>}
        </div>
        <div>
          <label htmlFor={`${titleId}-name`} className="mb-1 block text-sm font-medium text-v7-ink">Full Name</label>
          <input type="text" id={`${titleId}-name`} value={isReady ? formData.name : user?.name ?? ""}
            disabled={loading || dataLoading || !isReady}
            onChange={e => setFormData(previous => ({ ...previous, name: e.target.value }))}
            className={alphaAdminUi.input} placeholder="Full name" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-v7-panel border border-v7-ink/15 bg-v7-paper p-4">
          <div><p className="text-xs font-medium uppercase tracking-wider text-v7-ink/60">Access</p>
            <p className="mt-1 text-sm text-v7-ink">{isReady ? formData.roles.join(", ") || "No roles" : "Loading..."}</p></div>
          {(mode === "create" || onManagePermissions) && <button type="button" className={alphaAdminUi.link} aria-haspopup="dialog" disabled={loading || dataLoading || !isReady || profileDirty}
            onClick={() => mode === "create" ? setChoosingRoles(true) : onManagePermissions?.()}>Roles and scopes</button>}
        </div>
        {profileDirty && onManagePermissions && <p className="text-xs text-v7-ink/60">Save your profile changes before editing roles and scopes.</p>}
        {mode === "create" && <p className="text-xs text-v7-ink/60">New users receive the default access for each selected role. After creation, open Roles and scopes to choose narrower access.</p>}
        {isReady && formData.roles.includes("PROVIDER") && <div>
          <label htmlFor={`${titleId}-provider`} className="mb-1 block text-sm font-medium text-v7-ink">Provider</label>
          <select id={`${titleId}-provider`} value={formData.providerId} disabled={loading || dataLoading || !isReady || !canEditPermissions}
            onChange={e => setFormData(previous => ({ ...previous, providerId: e.target.value }))} className={alphaAdminUi.input}>
            <option value="">Select a provider</option>
            {formData.providerId && !providers.some(provider => provider.id === formData.providerId) && <option value={formData.providerId}>{initialUser?.provider?.name ?? formData.providerId}</option>}
            {providers.map(provider => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
          </select>
          {!formData.providerId && <p className="mt-1 text-xs text-v7-ink/60">Select a provider before saving.</p>}
        </div>}
        <label className="flex items-center gap-3 text-sm text-v7-ink">
          <input type="checkbox" checked={formData.emailVerified} disabled={loading || dataLoading || !isReady}
            onChange={e => setFormData(previous => ({ ...previous, emailVerified: e.target.checked }))}
            className="h-4 w-4 accent-v7-bronze-dark focus:ring-v7-bronze-dark" />Email Verified
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-v7-ink/10 pt-4">
          <div>{mode === "edit" && onDelete && <button type="button" onClick={() => setShowDeleteConfirm(true)} disabled={loading} className={alphaAdminUi.danger}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />Delete</button>}</div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={loading} className={alphaAdminUi.secondary}>Cancel</button>
            <button type="submit" disabled={isSaveDisabled} className={alphaAdminUi.primary}><Save className="h-4 w-4" aria-hidden="true" />
              {loading ? "Saving..." : mode === "create" ? "Create User" : "Save Changes"}</button>
          </div>
        </div>
      </form>
    </AlphaSettingsModal>
    <UserPermissionsModal isOpen={isOpen && choosingRoles} mode="create" initialRoles={formData.roles}
      onClose={() => setChoosingRoles(false)} onSave={async data => {
        setFormData(previous => ({ ...previous, roles: data.roles ?? previous.roles,
          providerId: data.roles?.includes("PROVIDER") ? previous.providerId : "" }));
        setChoosingRoles(false);
      }} />
    <ConfirmationModal appearance="alpha" isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={confirmDelete}
      title="Delete User" message={`Are you sure you want to delete user "${user?.email}"?`} confirmText="Delete" isDestructive loading={loading} />
  </>;
};

export default UserModal;
